import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Action, State, StateContext, Store } from '@ngxs/store';
import { firstValueFrom, from, Observable } from 'rxjs';
import { catchError, distinctUntilChanged, filter, switchMap, tap } from 'rxjs/operators';
import {
  AppInitDone,
  FetchAdfsPlants,
  GetGlobalNotificationsByFilter,
  GetGlobalNotificationsByFilterError,
  GetGlobalNotificationsByFilterSuccess,
  LoadConfig,
  LoadConfigError,
  LoadConfigSuccess,
  LoginAdfs,
  LoginAdfsSuccess,
  LoginApplicationDomain,
  LoginErrorDomain,
  LoginSuccessDomain,
  LogoutAdfs,
  SetActivePlant,
  SetLoading,
  SetManageCommitsRights,
  SetTenant,
  SetUserRights,
  SetUserRightsByRole,
  SetUserRightsSuccess,
  SetUserRole,
  SetUserRoles,
} from './authentication.actions';

import { AuthenticatedUser, DateTypeRights, UserRights } from './authenticated-user.model';
import { AuthenticationService } from './authentication.service';

import { Injectable } from '@angular/core';
import { ConfigService, Plant } from '@orion-platform/core';
import { environment } from '@orion/environment';
import { UserRightsByRole } from 'apps/orion/src/app/features/scm/material-management/v1/consts/user-rights';
import {
  NotificationsByFilterPayload,
  NotificationsResult,
} from 'apps/orion/src/app/features/scm/material-management/v1/store/global-notifications/global-notifications.types';
import { UserRoles } from 'apps/orion/src/app/features/scm/material-management/v1/types';
import { Helper } from '../../../../../apps/orion/src/app/helper/helper';
import {
  carriersStorageRoles,
  dashboardRoles,
  defaultState,
  financialRestrictedMenuItems,
  LAST_URL_BEFORE_LOGOUT_KEY,
  logisticsDocsAccessibleRoles,
  logisticsInstructionAccessibleRoles,
  multiCommitsRoles,
  portfolioRoles,
  restrictedFinancialMenuRoles,
  rolesList,
  slotRoles,
  slotStorageRoles,
} from './authentication.consts';
import { AuthenticationStateModel } from './authentication.types';
import {
  intersectRights,
  isUserRole,
  readLastLoggedInPrimarySid,
  resolveActivePlant,
  writeLastLoggedInPrimarySid,
} from './authentication.utils';

@State<AuthenticationStateModel>({
  defaults: defaultState,
  name: 'authentication',
})
@Injectable()
export class AuthenticationState {
  constructor(
    private readonly authService: AuthenticationService,
    private readonly http: HttpClient,
    private readonly configService: ConfigService,
    private readonly store: Store,
    private readonly router: Router,
  ) {}

  @Action(AppInitDone)
  appInitDone({ dispatch, patchState }: StateContext<AuthenticationStateModel>): void {
    patchState({ isAppReady: true });

    this.authService
      .getIsAuthorized()
      .pipe(
        distinctUntilChanged(
          (prev, curr): boolean => prev?.isAuthenticated === curr?.isAuthenticated,
        ),
        filter((isAuthorized): boolean => !!isAuthorized?.isAuthenticated),
      )
      .subscribe(async (): Promise<void> => {
        const upn = await this.authService.getUpn();
        const username = await this.authService.getUserName();
        const primarySid = await this.authService.getPrimarySid();

        dispatch(new LoadConfig());
        dispatch(new LoginAdfsSuccess(username, upn, primarySid));
      });
  }

  /** This action is triggered immediately after the user logs in.
   * Currently, it only loads the list of available plants from the backend.
   * However, the action is designed and prepared to be extended in the future
   * to load all configuration data available to the authenticated user
   * (e.g., user settings, permissions, and environment configurations).
   * Loading the plants right after login is crucial, because missing or delayed
   * plant data causes errors in the application workflow.
   *
   * TODO: In the future, the system should not proceed to load or initialize any other parts
   * of the application until all configurations have been successfully loaded.
   */
  @Action(LoadConfig)
  getColumns({ dispatch }: StateContext<AuthenticationStateModel>): Observable<void> {
    this.authService.snapshotForDiagnostics('LoadConfig:before-fetch');
    return from(this.authService.getActivePlants()).pipe(
      switchMap((plants: Plant[]): Observable<void> => dispatch([new LoadConfigSuccess(plants)])),
      catchError((err): Observable<void> => {
        this.authService.snapshotForDiagnostics('LoadConfig:fetch-error', {
          errorMessage: err && err.message,
          errorStatus: err && err.status,
        });
        return dispatch(new LoadConfigError(err));
      }),
    );
  }

  @Action(LoadConfigSuccess)
  loadConfigSuccess(
    { dispatch, getState, patchState }: StateContext<AuthenticationStateModel>,
    { plants }: LoadConfigSuccess,
  ): void {
    this.authService.snapshotForDiagnostics('LoadConfigSuccess:received', {
      receivedPlantsCount: Array.isArray(plants) ? plants.length : null,
      receivedPlantsKind: Array.isArray(plants) ? 'array' : typeof plants,
    });
    // BE can return empty plants on re-login (race before JWT is in interceptor); skip
    // overwrite so persisted plant/plants survive and the plant filter still applies.
    if (!Array.isArray(plants) || plants.length === 0) {
      return;
    }
    // Revalidate the persisted plant against the fresh list from BE — drops a stale
    // plant left in localStorage by a previous user / by since-revoked rights.
    const { plant } = getState();
    patchState({ plant: resolveActivePlant(plant, plants), plants });
    dispatch(new SetUserRightsByRole());
  }

  @Action(SetUserRights)
  setUserRights(
    { dispatch, patchState }: StateContext<AuthenticationStateModel>,
    { userRights }: SetUserRights,
  ): void {
    patchState({
      carriersModuleRights: userRights.carriersModuleRights,
      commitsModuleRights: userRights.commitsModuleRights,
      configurationRights: userRights.configurationRights,
      contactsRights: userRights.contactsRights,
      dashboardRights: userRights.dashboardRights,
      dateTypeRightList: userRights.dateTypeRightList,
      dateTypeRights: userRights.dateTypeRights,
      finantialModuleRights: userRights.finantialModuleRights,
      generateInstructionsRights: userRights.generateInstructionsRights,
      menuAccessRightList: userRights.menuAccessRightList,
      portfolioRights: userRights.portfolioRights,
      shipmentsModuleRights: userRights.shipmentsModuleRights,
      slotsCalendarRights: userRights.slotsCalendarRights,
      slotsModuleRights: userRights.slotsModuleRights,
      slotsStorageModuleRights: userRights.slotsStorageModuleRights,
      statusRightList: userRights.statusRightList,
      stocksModuleRights: userRights.stocksModuleRights,
      supplyVisibilityRights: userRights.supplyVisibilityRights,
      uploadDocumentsRights: userRights.uploadDocumentsRights,
      vendorListRights: userRights.vendorListRights,
      widgetAccessRightList: userRights.widgetAccessRightList,
    });
    dispatch(new SetUserRightsSuccess(userRights));
  }

  @Action(SetManageCommitsRights)
  setManageCommitsRights(
    { getState, patchState }: StateContext<AuthenticationStateModel>,
    { permission }: SetManageCommitsRights,
  ): void {
    const state = getState();
    const commitsRights = state.commitsModuleRights;
    const svRights = state.supplyVisibilityRights;
    const dashRights = state.dashboardRights;
    const venlRights = state.vendorListRights;
    const confRights = state.configurationRights;
    const finRight = state.finantialModuleRights;
    const dateRite = state.dateTypeRights;
    const dateListRight = state.dateTypeRightList;
    const menuRight = state.menuAccessRightList;
    const WidgetRight = state.widgetAccessRightList;
    const statusListRight = state.statusRightList;

    if (commitsRights) {
      commitsRights['deleteCommits'] = permission;
    }

    if (svRights) {
      svRights['deleteCommits'] = permission;
    }

    patchState({
      commitsModuleRights: commitsRights,
      configurationRights: confRights,
      dashboardRights: dashRights,
      dateTypeRightList: dateListRight,
      dateTypeRights: dateRite,
      finantialModuleRights: finRight,
      menuAccessRightList: menuRight,
      statusRightList: statusListRight,
      supplyVisibilityRights: svRights,
      vendorListRights: venlRights,
      widgetAccessRightList: WidgetRight,
    });
  }

  @Action(SetLoading)
  setLoading(
    { patchState }: StateContext<AuthenticationStateModel>,
    { loading }: SetLoading,
  ): void {
    patchState({ loading });
  }

  @Action(LoginAdfs)
  loginAdfs({ patchState }: StateContext<AuthenticationStateModel>): void {
    patchState({ loading: true });
    this.authService.loginAdfs();
  }

  @Action(LoginAdfsSuccess)
  loginAdfsSuccess(
    { patchState }: StateContext<AuthenticationStateModel>,
    { primarySid, upn, username }: LoginAdfsSuccess,
  ): void {
    // PO decision: cross-user login resets plant + URL completely. Plant goes to the new
    // user's first allowed plant (via getDefaultPlant in LoadConfigSuccess after `plant`
    // is cleared here), URL goes to /dashboard (or first menu item if no widgets). The
    // previous user's saved URL is dropped so the new user doesn't land on it.
    const previousSid = readLastLoggedInPrimarySid();
    const userChanged = !!previousSid && previousSid !== primarySid;
    const basePatch = {
      isLoggedIn: true,
      loading: false,
      primarySid,
      upn,
      username,
    };
    if (userChanged) {
      patchState({
        ...basePatch,
        menuAccessRightList: [],
        plant: '',
        plants: [],
        widgetAccessRightList: [],
      });
      try {
        localStorage.removeItem(LAST_URL_BEFORE_LOGOUT_KEY);
      } catch {
        // ignore storage access errors.
      }
    } else {
      patchState(basePatch);
    }
    writeLastLoggedInPrimarySid(primarySid);
  }

  @Action(LogoutAdfs)
  LogoutAdfs({ getState, setState }: StateContext<AuthenticationStateModel>): Promise<void> {
    const { activeTenant, plant, plants, primarySid } = getState();
    writeLastLoggedInPrimarySid(primarySid);
    setState({
      activeTenant: activeTenant,
      applicationForm: null,
      isAppReady: false,
      isLoggedIn: false,
      loading: false,
      plant: plant,
      plants: plants,
      primarySid: '',
      tenants: [],
      token: '',
      upn: '',
      username: '',
    });
    this.authService.resetPlantsCache();
    return this.authService.logoutAdfs();
  }

  @Action(SetUserRightsByRole)
  async setUserRightsByRole({
    dispatch,
    getState,
  }: StateContext<AuthenticationStateModel>): Promise<void> {
    const groupNames: UserRoles[] = await this.authService.getGroupNames();
    let userRights: UserRights = {};
    let datetypes: DateTypeRights = {};
    let datetypeslist: string[] = [];
    let menuAccessRightList: string[] = [];
    let widgetAccessRightList: string[] = [];
    let statusRightList: string[] = [];

    /* hotfix roll back - issues with redirecting */
    if (!groupNames || groupNames.length === 0) {
      setTimeout((): void => {
        this.router.navigate(['/unauthorized'], { replaceUrl: true });
        return;
      }, 0);
    }

    this.store.dispatch(
      new SetUserRoles(groupNames.filter((role): boolean => rolesList.includes(role))),
    );

    for (let groupNamesIndex = 0; groupNamesIndex < groupNames.length; groupNamesIndex++) {
      if (rolesList.includes(groupNames[groupNamesIndex])) {
        const userRole = groupNames[groupNamesIndex] as UserRoles;
        const rightsConfig = UserRightsByRole[userRole];

        this.store.dispatch(new SetUserRole(groupNames[groupNamesIndex]));
        datetypes = {
          ...userRights.dateTypeRights,
          ...Helper.getUserRightByRole(groupNames[groupNamesIndex]).dateTypeRights,
        };
        const tempRoleDateTypeList: string[] | undefined = Helper.getUserRightByRole(
          groupNames[groupNamesIndex],
        ).dateTypeRightList;
        if (tempRoleDateTypeList) {
          datetypeslist = datetypeslist.concat(tempRoleDateTypeList);
        }

        const tempRoleMenuAccessRightList: string[] | undefined = Helper.getUserRightByRole(
          groupNames[groupNamesIndex],
        ).menuAccessRightList;
        if (tempRoleMenuAccessRightList) {
          menuAccessRightList = menuAccessRightList.concat(tempRoleMenuAccessRightList);
          if (groupNames.includes(UserRoles.Orion_Administrator)) {
            menuAccessRightList.push('Global Notifications');
          }
        }

        const tempRoleWidgetAccessRightList: string[] | undefined = Helper.getUserRightByRole(
          groupNames[groupNamesIndex],
        ).widgetAccessRightList;
        if (tempRoleWidgetAccessRightList) {
          widgetAccessRightList = widgetAccessRightList.concat(tempRoleWidgetAccessRightList);
        }

        const tempRoleStatusList: string[] | undefined = Helper.getUserRightByRole(
          groupNames[groupNamesIndex],
        ).statusRightList;
        if (tempRoleStatusList) {
          statusRightList = statusRightList.concat(tempRoleStatusList);
        }

        //userRights = { ...userRights, ...Helper.getUserRightByRole(groupNames[groupNamesIndex]) } /*issue with concat as group structure*/

        userRights.commitsModuleRights = {
          ...userRights.commitsModuleRights,
          ...Helper.getUserRightByRole(groupNames[groupNamesIndex]).commitsModuleRights,
        };
        userRights.configurationRights = {
          ...userRights.configurationRights,
          ...Helper.getUserRightByRole(groupNames[groupNamesIndex]).configurationRights,
        };
        userRights.dashboardRights = {
          ...userRights.dashboardRights,
          ...Helper.getUserRightByRole(groupNames[groupNamesIndex]).dashboardRights,
        };
        userRights.finantialModuleRights = {
          ...userRights.finantialModuleRights,
          ...Helper.getUserRightByRole(groupNames[groupNamesIndex]).finantialModuleRights,
        };
        userRights.supplyVisibilityRights = {
          ...userRights.supplyVisibilityRights,
          ...Helper.getUserRightByRole(groupNames[groupNamesIndex]).supplyVisibilityRights,
        };
        userRights.vendorListRights = {
          ...userRights.vendorListRights,
          ...Helper.getUserRightByRole(groupNames[groupNamesIndex]).vendorListRights,
        };
        userRights.widgetRights = {
          ...userRights.widgetRights,
          ...Helper.getUserRightByRole(groupNames[groupNamesIndex]).widgetRights,
        };

        userRights.dateTypeRights = datetypes;
        userRights.dateTypeRightList = datetypeslist;
        userRights.menuAccessRightList = menuAccessRightList;
        userRights.widgetAccessRightList = widgetAccessRightList;

        userRights.statusRightList = statusRightList;
        userRights.carriersModuleRights = {
          ...userRights.carriersModuleRights,
          ...Helper.getUserRightByRole(groupNames[groupNamesIndex]).carriersModuleRights,
        };
        userRights.stocksModuleRights = {
          ...userRights.stocksModuleRights,
          ...Helper.getUserRightByRole(groupNames[groupNamesIndex]).stocksModuleRights,
        };
        userRights.shipmentsModuleRights = {
          ...userRights.shipmentsModuleRights,
          ...Helper.getUserRightByRole(groupNames[groupNamesIndex]).shipmentsModuleRights,
        };
        userRights.slotsStorageModuleRights = {
          ...userRights.slotsStorageModuleRights,
          ...Helper.getUserRightByRole(groupNames[groupNamesIndex]).slotsStorageModuleRights,
        };
        userRights.slotsModuleRights = {
          ...userRights.slotsModuleRights,
          ...(rightsConfig && rightsConfig.slotsPage),
          ...Helper.getUserRightByRole(groupNames[groupNamesIndex]).slotsModuleRights,
        };
        userRights.contactsRights = {
          ...userRights.contactsRights,
          ...(rightsConfig && rightsConfig.contactsPage),
        };
        userRights.slotsCalendarRights = {
          ...userRights.slotsCalendarRights,
          ...(rightsConfig && rightsConfig.slotsCalendar),
        };
        userRights.uploadDocumentsRights = {
          ...userRights.uploadDocumentsRights,
          ...(rightsConfig && rightsConfig.uploadDocumentsPage),
          ...Helper.getUserRightByRole(groupNames[groupNamesIndex]).uploadDocumentsRights,
        };
        userRights.generateInstructionsRights = {
          ...userRights.generateInstructionsRights,
          ...(rightsConfig && rightsConfig.generateInstructions),
          ...Helper.getUserRightByRole(groupNames[groupNamesIndex]).generateInstructionsRights,
        };
        userRights.portfolioRights = {
          ...userRights.portfolioRights,
          ...(rightsConfig && rightsConfig.portfolioRights),
        };

        //this.store.dispatch(new SetUserRights(userRights));
        //this.writeLogs ? this.writeLog('setUserRights', userRights) : null;
        //break;
      }
      if (
        groupNames[groupNamesIndex] === UserRoles.Orion_SCM_Supplier_Commit ||
        groupNames[groupNamesIndex] === UserRoles.Orion_SCM_Supplier_BS
      ) {
        this.store.dispatch(new SetManageCommitsRights(true));
      }
    }

    const state = getState();

    const activePlant = resolveActivePlant(state.plant, state.plants).toLowerCase();

    switch (activePlant) {
      case 'multi': {
        const rights = Helper.getUserRightByRole('Orion_Multi');

        const menuSet = new Set(
          (userRights.menuAccessRightList || []).filter((value): boolean =>
            (rights.menuAccessRightList || []).includes(value),
          ),
        );

        const widgetSet = new Set(
          (userRights.widgetAccessRightList || []).filter((value): boolean =>
            (rights.widgetAccessRightList || []).includes(value),
          ),
        );

        if (logisticsDocsAccessibleRoles.some((role): boolean => groupNames.includes(role))) {
          menuSet.add('Upload Documents');
        }

        if (
          logisticsInstructionAccessibleRoles.some((role): boolean => groupNames.includes(role))
        ) {
          menuSet.add('Generate Instructions');
        }

        if (groupNames.includes(UserRoles.Orion_Logistics_Warehouse)) {
          menuSet.add('Shipments');
        }

        const hasRestrictedFinancialMenuRole = restrictedFinancialMenuRoles.some((role): boolean =>
          groupNames.includes(role),
        );

        financialRestrictedMenuItems.forEach((menuItem): void => {
          menuSet.delete(menuItem);

          if (!hasRestrictedFinancialMenuRole) {
            menuSet.add(menuItem);
          }
        });

        menuSet.delete('Stocks');

        const canSearchCommits = multiCommitsRoles.some((role): boolean =>
          groupNames.includes(role),
        );
        if (canSearchCommits) {
          menuSet.add('Commits');
          widgetSet.add('commitList');
        } else {
          menuSet.delete('Commits');
          widgetSet.delete('commitList');
        }

        userRights.menuAccessRightList = Array.from(menuSet);
        userRights.widgetAccessRightList = Array.from(widgetSet);

        break;
      }
      case 'jusda':
      case 'ctp': {
        const plantRole = activePlant === 'jusda' ? 'Orion_JUSDA' : 'Orion_CTP';
        const rights = Helper.getUserRightByRole(plantRole);
        const plantStructuredRights = UserRightsByRole[plantRole as UserRoles];

        const menuSet = new Set(
          (userRights.menuAccessRightList || []).filter((value): boolean =>
            (rights.menuAccessRightList || []).includes(value),
          ),
        );

        const widgetSet = new Set(
          (userRights.widgetAccessRightList || []).filter((value): boolean =>
            (rights.widgetAccessRightList || []).includes(value),
          ),
        );

        const hasWarehouse = groupNames.includes(UserRoles.Orion_Logistics_Warehouse);

        const hasElevatedLogisticsRole =
          slotStorageRoles.some((r): boolean => groupNames.includes(r)) ||
          logisticsDocsAccessibleRoles.some((r): boolean => groupNames.includes(r)) ||
          carriersStorageRoles.some((r): boolean => groupNames.includes(r)) ||
          slotRoles.some((r): boolean => groupNames.includes(r));

        const warehouseOnlyMode = hasWarehouse && !hasElevatedLogisticsRole;

        if (warehouseOnlyMode) {
          menuSet.add('Slots');
          menuSet.add('Slot Calendar');
          menuSet.add('Shipments');

          menuSet.delete('Slots Storage');
          menuSet.delete('Upload Documents');
          menuSet.delete('Carriers');
        } else {
          if (hasWarehouse) {
            menuSet.add('Slots');
            menuSet.add('Slot Calendar');
            menuSet.add('Shipments');
          }

          if (logisticsDocsAccessibleRoles.some((role): boolean => groupNames.includes(role))) {
            menuSet.add('Upload Documents');
          }

          if (slotStorageRoles.some((role): boolean => groupNames.includes(role))) {
            menuSet.add('Slots Storage');
            menuSet.add('Slot Calendar');
          }

          if (slotRoles.some((role): boolean => groupNames.includes(role))) {
            menuSet.add('Slots');
          }

          if (carriersStorageRoles.some((role): boolean => groupNames.includes(role))) {
            menuSet.add('Carriers');
          }
        }

        menuSet.delete('Stocks');

        if (portfolioRoles.some((role): boolean => groupNames.includes(role))) {
          menuSet.add('Portfolio');
        }

        userRights.dashboardRights = {
          ...userRights.dashboardRights,
          dashboardEdit:
            isUserRole(state.currentRole) && dashboardRoles.includes(state.currentRole),
        };

        userRights.portfolioRights = intersectRights(
          userRights.portfolioRights,
          plantStructuredRights && plantStructuredRights.portfolioRights,
        ) as typeof userRights.portfolioRights;

        userRights.menuAccessRightList = Array.from(menuSet);
        userRights.widgetAccessRightList = Array.from(widgetSet);

        break;
      }
      default:
        userRights.widgetAccessRightList = (userRights.widgetAccessRightList || []).filter(
          (widget): boolean => widget !== 'slotList',
        );
      // Add more cases as needed
    }

    dispatch(new SetUserRights(userRights));
  }

  @Action(LoginApplicationDomain)
  loginApplicationDomain(
    { dispatch }: StateContext<AuthenticationStateModel>,
    { domain, password, plant, username }: LoginApplicationDomain,
  ): Observable<AuthenticatedUser | void> {
    return this.authService.loginApplicationDomain({ domain, password, plant, username }).pipe(
      tap(({ token }: AuthenticatedUser): void => {
        if (token) {
          dispatch(new LoginSuccessDomain(token, plant, username));
        }
      }),
      catchError((): Observable<void> => dispatch(new LoginErrorDomain())),
    );
  }

  @Action(LoginSuccessDomain)
  loginSuccessDomain(
    { patchState }: StateContext<AuthenticationStateModel>,
    { plant, token, username }: LoginSuccessDomain,
  ): void {
    patchState({
      isLoggedIn: true,
      plant: plant,
      token: token,
      username: username,
    });
  }

  @Action(LoginErrorDomain)
  loginCanceledDomain(): void {}

  // @Action(LogoutDomain)
  // logoutDomain({
  //   getState,
  //   setState,
  // }: StateContext<AuthenticationStateModel>): Observable<unknown> {
  //   const { plant, plants, activeTenant } = getState();

  //   return this.authService.logoutDomain().pipe(
  //     tap(() => {
  //       setState({
  //         isAppReady: false,
  //         token: '',
  //         tenants: [],
  //         activeTenant: activeTenant,
  //         isLoggedIn: false,
  //         applicationForm: null,
  //         username: '',
  //         upn: '',
  //         primarySid: '',
  //         plant: plant,
  //         plants: plants,
  //       });
  //     }),
  //   );
  // }

  // @Action(FetchPlants)
  // async fetchPlants({ patchState }: StateContext<AuthenticationStateModel>): void {
  //   const result: any = this.authService.fetchPlants();
  //   let plants: Plant[] = [];

  //   if (result && typeof result.subscribe === 'function') {
  //     // Observable<Plant[]>
  //     plants = await firstValueFrom(result as Observable<Plant[]>);
  //   } else {
  //     // Promise<Plant[]> or Plant[]
  //     plants = await Promise.resolve(result as Plant[]);
  //   }

  //   patchState({ plants });
  // }

  @Action(FetchAdfsPlants)
  async fetchAfdsPlants({
    getState,
    patchState,
  }: StateContext<AuthenticationStateModel>): Promise<void> {
    // fetch the fresh plant list the current user has rights to from BE
    const fresh: Plant[] = await this.authService.getActivePlants();
    // pull the current plant + plants list from state (hydrated from localStorage — may be stale)
    const { plant: cachedPlant, plants: cachedPlants } = getState();

    // if a non-empty list, use it; otherwise fall back to the cached one (BE outage / network error)
    const plants: Plant[] = fresh && fresh.length ? fresh : (cachedPlants as Plant[]) || [];

    // write the new plants list + a revalidated plant through resolveActivePlant
    // (drop stale `cachedPlant` if it isn't Show:true in the current list → fall back to the first visible, or '')
    patchState({
      plant: resolveActivePlant(cachedPlant, plants),
      plants,
    });
  }
  // ...existing code...

  // TODO move function to a service.
  private async getGlobalNotificationByFilter(
    filter: NotificationsByFilterPayload,
  ): Promise<NotificationsResult> {
    const apiUrl = this.configService.getSettings('userSettingsService') as string;
    const baseUrl = apiUrl.endsWith('/') ? apiUrl : `${apiUrl}/`;
    const endpoint = environment.modulesBaseUrl.materialManagement.globalNotifications;
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint.substring(1) : endpoint;

    const result = await firstValueFrom(
      this.http.post(`${baseUrl}${cleanEndpoint}/ByFilter`, filter),
    );
    return result as NotificationsResult;
  }

  // Validates against the current `plants` list so a `Show:false` (or unknown) plant can never become active;
  // falls back to the first visible plant, or '' if none exists.
  @Action(SetActivePlant)
  setActivePlant(
    { getState, patchState }: StateContext<AuthenticationStateModel>,
    { plant }: SetActivePlant,
  ): void {
    const { plants } = getState();
    patchState({
      plant: resolveActivePlant(plant, plants),
    });
  }

  @Action(GetGlobalNotificationsByFilter)
  async getGlobalNotificationsByFilter(
    { dispatch }: StateContext<AuthenticationStateModel>,
    { filter }: GetGlobalNotificationsByFilter,
  ): Promise<void> {
    const notifications = await this.getGlobalNotificationByFilter(filter);

    if (notifications) {
      dispatch(new GetGlobalNotificationsByFilterSuccess(notifications));
    } else {
      dispatch(new GetGlobalNotificationsByFilterError('error'));
    }
  }

  @Action(SetUserRoles)
  setUserRoles(
    { patchState }: StateContext<AuthenticationStateModel>,
    { roles }: SetUserRoles,
  ): void {
    patchState({
      currentRoles: roles,
    });
  }

  @Action(SetTenant)
  setTenant({ patchState }: StateContext<AuthenticationStateModel>, { tenant }: SetTenant): void {
    patchState({
      activeTenant: tenant,
    });
  }
}
